/**
 * LEOS Mission Ready - User Profile Popup
 * Compact popup showing user account information
 */

import { unifiedAuth } from '../auth/unified-auth.js';

export class UserProfileOverlay {
    constructor(user) {
        this.isVisible = false;
        this.popup = null;
        this.user = user || unifiedAuth.currentUser;
        console.log('UserProfileOverlay - User:', this.user);
        this.init();
    }

    init() {
        this.createPopup();
        this.attachEventListeners();
        this.addStyles();
    }

    createPopup() {
        // Create popup container
        this.popup = document.createElement('div');
        this.popup.className = 'user-profile-popup';
        this.popup.style.display = 'none';
        
        // Calculate account details
        const email = `${this.user?.username || 'user'}@${this.user?.company?.toLowerCase().replace(/\s+/g, '') || 'cyberrts'}.com`;
        const tierDisplay = this.user?.tier === 'mission-ready' ? 'Mission Ready' : 'First Orbit';
        
        this.popup.innerHTML = `
            <!-- Header -->
            <div class="popup-header">
                <div class="popup-user-info">
                    <img src="${this.user?.avatar || 'assets/kelly.svg'}" alt="${this.user?.name}" class="popup-avatar" />
                    <div class="popup-user-details">
                        <h3>${this.user?.name || 'User'}</h3>
                        <p class="popup-role">${this.user?.role || 'Flight Director'}</p>
                        <p class="popup-email">${email}</p>
                    </div>
                </div>
            </div>

            <!-- Quick Stats -->
            <div class="popup-section popup-stats">
                <div class="stat-row">
                    <span class="stat-label">Subscription</span>
                    <span class="stat-value tier-${this.user?.tier || 'starter'}">${tierDisplay}</span>
                </div>
                <div class="stat-row">
                    <span class="stat-label">Status</span>
                    <span class="stat-value status-active">Active</span>
                </div>
            </div>

            <!-- Usage Overview -->
            <div class="popup-section popup-usage">
                <h4>Usage Overview</h4>
                <div class="usage-grid">
                    <div class="usage-item">
                        <span class="usage-label">Active Missions</span>
                        <span class="usage-value">${this.user?.missions?.active || 0}</span>
                    </div>
                    <div class="usage-item">
                        <span class="usage-label">Satellites</span>
                        <span class="usage-value">${this.user?.satellites?.active || 0}</span>
                    </div>
                    <div class="usage-item">
                        <span class="usage-label">Ground Stations</span>
                        <span class="usage-value">12</span>
                    </div>
                    <div class="usage-item">
                        <span class="usage-label">Data Today</span>
                        <span class="usage-value">1.2TB</span>
                    </div>
                </div>
            </div>

            <!-- Team Members (if applicable) -->
            ${this.user?.team && this.user.team.length > 0 ? `
            <div class="popup-section popup-team">
                <h4>Team Members (${this.user.team.length})</h4>
                <div class="team-list">
                    ${this.user.team.slice(0, 3).map(member => `
                        <div class="team-member-compact">
                            <div class="member-avatar-compact">
                                <span>${member.name.split(' ').map(n => n[0]).join('')}</span>
                            </div>
                            <div class="member-info-compact">
                                <div class="member-name">${member.name}</div>
                                <div class="member-role">${member.role}</div>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
            ` : ''}

            <!-- Actions -->
            <div class="popup-actions">
                <button class="popup-action-btn settings-btn">
                    <img src="assets/email.svg" alt="Settings" />
                    <span>Account Settings</span>
                </button>
                
                <button class="popup-action-btn support-btn">
                    <img src="assets/support.svg" alt="Support" />
                    <span>Help & Support</span>
                </button>
                
                <div class="action-divider"></div>
                
                <button class="popup-action-btn signout-btn">
                    <span>Sign Out</span>
                </button>
            </div>
        `;
        
        // Append to body for proper positioning
        document.body.appendChild(this.popup);
    }

    attachEventListeners() {
        // Click outside to close
        document.addEventListener('click', (e) => {
            if (this.isVisible && !this.popup.contains(e.target) && !e.target.closest('#user-profile-section')) {
                this.hide();
            }
        });

        // ESC key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isVisible) {
                this.hide();
            }
        });

        // Action buttons
        const settingsBtn = this.popup.querySelector('.settings-btn');
        if (settingsBtn) {
            settingsBtn.addEventListener('click', () => {
                this.hide();
                // Navigate to settings
                if (window.missionInterface) {
                    window.missionInterface.showSection('settings');
                }
            });
        }

        const supportBtn = this.popup.querySelector('.support-btn');
        if (supportBtn) {
            supportBtn.addEventListener('click', () => {
                this.hide();
                window.open('https://www.cyberrts.com/support', '_blank');
            });
        }

        const signoutBtn = this.popup.querySelector('.signout-btn');
        if (signoutBtn) {
            signoutBtn.addEventListener('click', () => {
                this.hide();
                if (confirm('Are you sure you want to sign out?')) {
                    // Clear all auth-related storage
                    localStorage.removeItem('leos-user');
                    localStorage.removeItem('auth-mode');
                    localStorage.removeItem('leos-activity-log');
                    sessionStorage.clear();
                    
                    // Use unified auth logout if available
                    if (window.unifiedAuth && window.unifiedAuth.logout) {
                        window.unifiedAuth.logout();
                    }
                    
                    // Reload the current page - the auth system will show login
                    window.location.reload();
                }
            });
        }

        // Add hover effects for action buttons
        this.popup.querySelectorAll('.popup-action-btn').forEach(btn => {
            btn.addEventListener('mouseenter', () => {
                if (!btn.classList.contains('signout-btn')) {
                    btn.style.background = 'rgba(148, 163, 184, 0.1)';
                }
            });
            btn.addEventListener('mouseleave', () => {
                if (!btn.classList.contains('signout-btn')) {
                    btn.style.background = 'transparent';
                }
            });
        });
    }

    show() {
        if (this.isVisible) return;
        
        // Update position based on user profile location
        const userProfile = document.getElementById('user-profile-section');
        if (userProfile) {
            const rect = userProfile.getBoundingClientRect();
            const sidebar = document.querySelector('.sidebar-navigation');
            const isCollapsed = sidebar?.classList.contains('collapsed');
            
            // Position relative to viewport
            this.popup.style.position = 'fixed';
            this.popup.style.top = `${rect.bottom + 10}px`;
            
            if (isCollapsed) {
                this.popup.style.left = '90px';
            } else {
                this.popup.style.left = '20px';
            }
            
            // Ensure popup doesn't go off-screen
            const popupHeight = 400; // Approximate height
            if (rect.bottom + popupHeight > window.innerHeight) {
                this.popup.style.top = `${rect.top - popupHeight - 10}px`;
            }
        }
        
        this.popup.style.display = 'block';
        
        // Trigger reflow
        this.popup.offsetHeight;
        
        // Animate in
        setTimeout(() => {
            this.popup.classList.add('visible');
        }, 10);
        
        this.isVisible = true;
    }

    hide() {
        if (!this.isVisible) return;
        
        this.popup.classList.remove('visible');
        
        setTimeout(() => {
            this.popup.style.display = 'none';
            this.isVisible = false;
        }, 200);
    }

    addStyles() {
        if (document.getElementById('user-profile-popup-styles')) return;
        
        const styleSheet = document.createElement('style');
        styleSheet.id = 'user-profile-popup-styles';
        styleSheet.textContent = `
            /* User Profile Popup Styles */
            .user-profile-popup {
                position: fixed;
                width: 320px;
                background: rgba(15, 23, 42, 0.98);
                border: 1px solid rgba(148, 163, 184, 0.3);
                border-radius: 16px;
                padding: 0;
                z-index: 1000;
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 212, 255, 0.1);
                opacity: 0;
                transform: translateY(-10px) scale(0.95);
                transition: all 0.2s ease;
                overflow: hidden;
            }

            .user-profile-popup.visible {
                opacity: 1;
                transform: translateY(0) scale(1);
            }

            /* Header */
            .popup-header {
                padding: 20px;
                border-bottom: 1px solid rgba(148, 163, 184, 0.2);
                background: rgba(0, 0, 0, 0.3);
            }

            .popup-user-info {
                display: flex;
                align-items: center;
                gap: 16px;
            }

            .popup-avatar {
                width: 48px;
                height: 48px;
                border-radius: 50%;
                border: 2px solid rgba(0, 212, 255, 0.3);
                filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);
                background: rgba(0, 212, 255, 0.1);
            }

            .popup-user-details {
                flex: 1;
                min-width: 0;
            }

            .popup-user-details h3 {
                color: #e2e8f0;
                font-size: 16px;
                font-weight: 600;
                margin: 0 0 4px 0;
            }

            .popup-role {
                color: #00d4ff;
                font-size: 13px;
                margin: 0 0 2px 0;
            }

            .popup-email {
                color: #64748b;
                font-size: 12px;
                margin: 0;
                font-family: 'Consolas', 'Monaco', monospace;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            /* Sections */
            .popup-section {
                padding: 16px 20px;
                border-bottom: 1px solid rgba(148, 163, 184, 0.2);
            }

            .popup-section h4 {
                color: #e2e8f0;
                font-size: 13px;
                font-weight: 600;
                margin: 0 0 12px 0;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            /* Stats */
            .stat-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
            }

            .stat-row:last-child {
                margin-bottom: 0;
            }

            .stat-label {
                color: #64748b;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .stat-value {
                color: #e2e8f0;
                font-size: 13px;
                font-weight: 600;
            }

            .stat-value.tier-mission-ready {
                color: #00d4ff;
            }

            .stat-value.status-active {
                color: #10b981;
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .stat-value.status-active::before {
                content: '';
                width: 6px;
                height: 6px;
                background: #10b981;
                border-radius: 50%;
            }

            /* Usage */
            .usage-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 12px;
            }

            .usage-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }

            .usage-label {
                color: #94a3b8;
                font-size: 12px;
            }

            .usage-value {
                color: #00d4ff;
                font-size: 13px;
                font-weight: 600;
            }

            /* Team */
            .team-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .team-member-compact {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .member-avatar-compact {
                width: 28px;
                height: 28px;
                background: rgba(0, 153, 255, 0.2);
                border: 1px solid rgba(0, 153, 255, 0.3);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }

            .member-avatar-compact span {
                color: #00d4ff;
                font-size: 11px;
                font-weight: 600;
            }

            .member-info-compact {
                flex: 1;
                min-width: 0;
            }

            .member-name {
                color: #e2e8f0;
                font-size: 12px;
                font-weight: 500;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .member-role {
                color: #64748b;
                font-size: 11px;
            }

            /* Actions */
            .popup-actions {
                padding: 12px;
                background: rgba(0, 0, 0, 0.2);
            }

            .popup-action-btn {
                background: transparent;
                border: none;
                padding: 10px 12px;
                color: #e2e8f0;
                font-size: 13px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                justify-content: flex-start;
                border-radius: 8px;
            }

            .popup-action-btn img {
                width: 16px;
                height: 16px;
                filter: brightness(0) saturate(100%) invert(69%) sepia(8%) saturate(507%) hue-rotate(185deg) brightness(89%) contrast(87%);
            }

            .popup-action-btn:hover {
                background: rgba(148, 163, 184, 0.1);
            }

            .action-divider {
                height: 1px;
                background: rgba(148, 163, 184, 0.2);
                margin: 8px 0;
            }

            .popup-action-btn.signout-btn {
                color: #ef4444;
            }

            .popup-action-btn.signout-btn:hover {
                background: rgba(239, 68, 68, 0.1);
            }
        `;
        
        document.head.appendChild(styleSheet);
    }

    // Method to update user data
    updateUserData(newUserData) {
        this.user = newUserData;
        if (this.popup) {
            this.popup.remove();
            this.createPopup();
            this.attachEventListeners();
        }
    }
}